import * as browserTTS from "browserTTS";
import * as localAPI from "localAPI";
import UserModel from "models/UserModel";
import UserWordModel from "models/UserWordModel";
import * as languageMap from "shared/languageMap";
import * as utils from "shared/utils";
import * as textToSpeech from "textToSpeech";

export const config = {
  browserTTS: false,
};

type IntroChallengeType = "intro";

export const passiveChallengeTypes = [
  "passiveMultipleChoice",
  "activeMultipleChoice",
  // "pickWord",
  // "arrange",
  // "match",
];

type PassiveChallengeType = (typeof passiveChallengeTypes)[number];

// export const passivePlusChallengeTypes = ["activeMultipleChoice"];

export const activeChallengeTypes = [
  // "makeSentence",
  // There needs to be a separation here. The above are not really active recall, really passive+.
  "typing",
];

type ActiveChallengeType = (typeof activeChallengeTypes)[number];

export type ChallengeType =
  | IntroChallengeType
  | PassiveChallengeType
  | ActiveChallengeType;

export type NextStep =
  | {
      type: "show-feedback";
      correct: "yes" | "no" | "almost";
      message?: {
        title: string;
        body: string;
      };
      showLeniencySettings?: boolean;
      showTypingModeToggle?: boolean;
    }
  | {
      type: "allow-continue";
      // no feedback in this case
    }
  | {
      type: "continue";
    };

export interface ChallengeProps {
  // Generally this will be just one UserWord, but for certain challenge type it will be more
  userWord: UserWordModel;
  allUserWords: Backbone.Collection<UserWordModel>;
  skipped?: boolean;
  selectedContextIndex: number;
  introCardMode: "active" | "passive" | "delete";
  completedCallback: (nextStep: NextStep) => void;
  reportRecallEase: (ease: number) => void;
  enableCheck: (enabled: boolean) => void;
  setCardMode: (introCardMode: "active" | "passive" | "delete") => void;
  // This should be called whenever a challenge shows the explanation
  showingExplanation: () => void;
}

const regexpEscape = (input) => input.replace(/[-[\]/{}()*+?.\\^$|]/g, "\\$&");

export type ClozeSentencePart = {
  type: "normal" | "target";
  text: string;
};

export type ClozeSentence = ClozeSentencePart[];

export const getClozeSentence = (
  userWord: UserWordModel,
  index: number
): ClozeSentence => {
  const noWordBoundaries =
    languageMap.map[userWord.get("language")]?.noWordBoundaries;

  const fallbackResult: ClozeSentence = [
    {
      type: "target",
      text: userWord.get("word"),
    },
  ];

  if (!userWord.get("contexts")) {
    return fallbackResult;
  }

  const contextText = userWord.get("contexts")[index]?.text;

  if (!contextText) {
    return [
      {
        type: "normal",
        text: "No context available for ",
      },
      {
        type: "target",
        text: userWord.get("word"),
      },
      {
        type: "normal",
        text: ".",
      },
    ];
  }

  return buildClozeSentence(
    contextText,
    userWord.get("word"),
    noWordBoundaries
  );
};

/** Builds a cloze sentence from a text and a regexp. Finds all matching occurrences. */
export const buildClozeSentence = (
  text: string,
  word: string,
  noWordBoundaries: boolean
): ClozeSentence => {
  const regexp = noWordBoundaries
    ? new RegExp(`(${regexpEscape(word)})`, "ig")
    : new RegExp(`\\W(${regexpEscape(word)})\\W`, "ig");
  if (!regexp.global) {
    throw new Error("RegExp must be global");
  }

  const textWithPadding = ` ${text} `;

  const result: ClozeSentence = [];

  // We start at 1 to account for the padding
  let lastIndex = noWordBoundaries ? 0 : 1;

  let match: RegExpExecArray | null;
  while ((match = regexp.exec(textWithPadding)) !== null) {
    if (match.index > lastIndex) {
      result.push({
        type: "normal",
        text: textWithPadding.substring(
          lastIndex,
          match.index + (noWordBoundaries ? 0 : 1)
        ),
      });
    }
    lastIndex = match.index + match[1].length + (noWordBoundaries ? 0 : 1);
    result.push({
      type: "target",
      text: match[1],
    });
  }

  if (lastIndex < textWithPadding.length - (noWordBoundaries ? 0 : 1)) {
    result.push({
      type: "normal",
      text: textWithPadding.substring(
        lastIndex,
        textWithPadding.length - (noWordBoundaries ? 0 : 1)
      ),
    });
  }

  return result;
};

export const playTTS = (
  language: string,
  text: string,
  forceBrowserTTS?: boolean
) => {
  if (forceBrowserTTS || config.browserTTS) {
    browserTTS.playTTSSimple(language, text);
  } else {
    textToSpeech.play(language, text);
  }
};

export const normalize = (input: string) =>
  input
    .replace(/[-.,¡!?¿]+/g, " ")
    .replace(/[\u0060\u00b4\u2018\u2019\u201B\u2032\u2035]/g, "'")
    .replace(/[\u201c\u201d]/g, '"')
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

type Grade = "correct" | "typo" | "watch-accent" | "incorrect";

export const gradeTyping = (correct: string, typed: string): Grade => {
  const correctNormalized = normalize(correct);
  const typedNormalized = normalize(typed);

  if (correctNormalized === typedNormalized) {
    return "correct";
  }

  const correctAccentsNormalized = utils.normalizeAccents(correctNormalized);
  const typedAccentsNormalized = utils.normalizeAccents(typedNormalized);

  if (correctAccentsNormalized === typedAccentsNormalized) {
    return "watch-accent";
  }

  const editDistance = utils.levenshteinDistance(
    correctAccentsNormalized,
    typedAccentsNormalized
  );
  const maxPermittedEditDistance = correctNormalized.length > 6 ? 2 : 1;

  if (editDistance <= maxPermittedEditDistance) {
    return "typo";
  }

  return "incorrect";
};

type Leniency = "allowTypos" | "allowMissingAccents" | "strict";

// Temporary leniency storage for prototyping
export const setLeniency = (leniency: Leniency) => {
  localStorage.setItem("leniency", leniency);
};

export const getLeniency = (): Leniency => {
  return (localStorage.getItem("leniency") || "allowTypos") as Leniency;
};

export const hasNewPracticeModes = async (_user: UserModel) => {
  return !(await localAPI.isLocalMode());
};

export const showContextSource = false;

export const cardsPerSession = (user: UserModel) => {
  return {
    verySmall: 4,
    small: 8,
    medium: 15,
    large: 25,
    veryLarge: 50,
  }[user.get("practiceLength")];
};

export const buildSessionUrl = (user: UserModel, selectedBookID: string) => {
  if (selectedBookID) {
    return `/${user.get("currentLearningLanguage")}/flashcards/book/${selectedBookID}`;
  }

  return `/flashcards`;
};

export const practiceType = async (
  user: UserModel
): Promise<"new" | "blitz" | "classic"> => {
  return !(await hasNewPracticeModes(user))
    ? "classic"
    : (user.get("practiceType") as "new" | "classic") ?? "blitz";
};
